import styles from './index.module.css'

import { MdInfo } from '@react-icons/all-files/md/MdInfo'
import cn from 'clsx'
import React, { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { SHAREABLE_CHANNELS } from '@disco/constants/FeatureTypes'
import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import {
  loadChannelShareUrls,
  updateChannelShareForm,
} from '@disco/state-management/reducers/forms/channel-share'
import { useFeatureEnabled } from '@disco/state-management/selectors/business'

import Row from 'components/common/row'
import { Callout, Flex, Link as RadixLink } from 'components/ds'
import Alert from 'components/ui/alert'
import Button from 'components/ui/button'
import CopyUrlItem from 'components/ui/copy-url-item'
import InlineLink from 'components/ui/inline-link'
import Label from 'components/ui/label'
import LoadingIndicator from 'components/ui/loading-indicator'

import { useDispatch } from 'hooks/use-redux'

import type { ChannelShareFormProps } from '../../types'

interface UpdatePayload {
  channelId: number
  territories?: number[]
}
const PublicLink = (props: ChannelShareFormProps): React.ReactNode => {
  const [loading, setLoading] = useState(false)
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { channelId } = useParams<{ channelId: string }>()
  const shareableChannelsEnabled = useFeatureEnabled(SHAREABLE_CHANNELS)
  const { channelId: formChannelId, territories, user } = props
  const generateLink = useCallback(
    (id: number) => {
      setLoading(true)
      dispatch(
        loadChannelShareUrls(id, {
          exclude_playlist_types: props.exclusionList || [],
          sort: JSON.parse(props.sortParams),
          territory_ids: props.territories,
        }),
      )
    },
    [dispatch, props.exclusionList, props.sortParams, props.territories],
  )

  const handleGenerateClick = () => generateLink(parseInt(channelId!))

  const id = parseInt(channelId)
  useEffect(() => {
    const updateForm = async () => {
      if (channelId && !formChannelId) {
        let update: UpdatePayload = {
          channelId: id,
        }

        if (
          !territories &&
          user.business.private_metadata.territories_enabled
        ) {
          update = { ...update, territories: user.territories }
        }

        await dispatch(updateChannelShareForm(update))
        generateLink(id)
      }
    }

    updateForm()
  }, [
    id,
    territories,
    channelId,
    formChannelId,
    user.business.hostname,
    user.business.private_metadata.territories_enabled,
    user.territories,
    dispatch,
    generateLink,
  ])
  useEffect(() => {
    if (props.publicUrl) {
      setLoading(false)
    }
  }, [props.publicUrl])

  const onDisableShare = () =>
    navigate(`/explorer/channels/${props.group.id}/revoke-share`)

  const onPublicUrlCopy = () => {
    dispatch(
      sendMixpanelEvent('Copy public channel url', {
        Url: props.publicUrl,
      }),
    )
  }

  return (
    <div className={styles.ChannelsShare__PublicTabContainer}>
      {loading && shareableChannelsEnabled ? (
        <div className={styles.ChannelsShare__LoadingIndicator}>
          <LoadingIndicator />
        </div>
      ) : (
        <Flex direction="column" gap="2" width="100%">
          {!shareableChannelsEnabled && (
            <Row columnsSize={[12]}>
              <div>
                <Alert status="change" icon="info-outline">
                  <div>
                    Shared channels are only available on Pro and Enterprise
                    plans.
                  </div>
                  <InlineLink to="/settings/billing">Upgrade plan</InlineLink>
                </Alert>
              </div>
            </Row>
          )}
          {props.user.business.private_metadata.territories_enabled && (
            <Callout.Root color="amber">
              <Callout.Icon>
                <MdInfo />
              </Callout.Icon>
              <Callout.Text>
                The channel will be filtered by territories before it&#39;s
                sent.
                <br />
                <RadixLink
                  href={props.publicUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  See preview
                </RadixLink>
              </Callout.Text>
            </Callout.Root>
          )}
          {props.watermarking_warning && (
            <Row columnsSize={[12]}>
              <div>
                <Alert status="warning" icon="warning">
                  This channel contains a track in a watermarked playlist.
                  Please note tracks within channel share pages will only be
                  watermarked within their playlist page.{' '}
                  <Link to="/watermarking">Find out more.</Link>
                </Alert>
              </div>
            </Row>
          )}
          {!props.revokeShare ? (
            <Row columnsSize={[12]} marginBottom="small">
              <div
                className={cn({
                  [styles.ChannelsShare__ShareUrlDisabled]:
                    !shareableChannelsEnabled,
                })}
              >
                <CopyUrlItem
                  disabled={!shareableChannelsEnabled}
                  label="Anyone with the link"
                  url={shareableChannelsEnabled ? props.publicUrl : ''}
                  onCopy={onPublicUrlCopy}
                  onDisable={onDisableShare}
                />
              </div>
            </Row>
          ) : (
            <Row columnsSize={[12]} marginBottom="small">
              <div>
                <Label caption="Anyone with the link" />
                <Button
                  caption="Generate new link"
                  onClick={handleGenerateClick}
                />
              </div>
            </Row>
          )}
          {props.error && (
            <Row columnsSize={[12]} marginBottom="small">
              <div>
                <Alert icon="warning" status="warning">
                  {props.error}
                </Alert>
              </div>
            </Row>
          )}
        </Flex>
      )}
    </div>
  )
}

export default PublicLink
